import {
  defaultLocale,
  Language,
  languages,
  languageToIsoLocaleString,
  localeToIsoLocaleString,
} from '@lessonup/utils';
import { addWeeks } from 'date-fns/addWeeks';
import type { i18n as i18nType, InitOptions, PostProcessorModule, Resource } from 'i18next';
import { createInstance } from 'i18next';
import Locize from 'i18next-locize-backend';
import { compact, isArray } from 'lodash';
import { clearCookie, getCookieValueByName, setCookie } from '../../cookies/cookieUtils';
import { formalizeTranslation } from './formalizeTranslation';

export const FORMALIZATION_POST_PROCESSOR_NAME = 'formalizationPostProcessor';
export const FORMALIZATION_COOKIE_NAME = 'useFormalLanguage';

type SharedInitParams = {
  userLanguage: Language;
  useFormalLanguageOnLoad: boolean;
  // Note: This is a workaround
  // import { initReactI18next } from 'react-i18next'; is a problem for Next.js server components since it's a barrel file that also does some client component things
  // import { initReactI18next } from 'react-i18next/initReactI18next'; is a problem for Meteor since it's ESM in CJS context
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initReactI18next: any;
};

type ResourceInstanceParams = {
  resources: Resource;
  missingKeyHandler?: InitOptions['missingKeyHandler'];
};

export interface LocizeSettings {
  projectId: string;
  apiKey?: string;
  useSuspense?: boolean;
}

export const getUseFormalLanguageOnLoadFromCookies = () => !!getCookieValueByName(FORMALIZATION_COOKIE_NAME);

export interface CustomI18n extends i18nType {
  setFormalization: (useFormal: boolean) => void;
}

export function innerInitI18n({
  resources,
  userLanguage,
  locize,
  useFormalLanguageOnLoad,
  missingKeyHandler,
  initReactI18next,
}: {
  locize?: LocizeSettings;
} & ResourceInstanceParams &
  SharedInitParams): CustomI18n {
  const instance = locize
    ? locizeTranslationsInstance({ userLanguage, locize, useFormalLanguageOnLoad, initReactI18next })
    : resourcesInstance({ resources, userLanguage, useFormalLanguageOnLoad, missingKeyHandler, initReactI18next });

  function setFormalization(useFormal: boolean): void {
    const currentProcessors: string[] = compact(
      isArray(instance.options.postProcess) ? instance.options.postProcess : [instance.options.postProcess]
    );

    if (useFormal) {
      const expires = addWeeks(new Date(), 1);
      setCookie({
        name: FORMALIZATION_COOKIE_NAME,
        value: 'true',
        expires,
      });
      instance.options.postProcess = [...currentProcessors, FORMALIZATION_POST_PROCESSOR_NAME];
    } else {
      clearCookie(FORMALIZATION_COOKIE_NAME);
      instance.options.postProcess = currentProcessors.filter(
        (processor) => processor !== FORMALIZATION_POST_PROCESSOR_NAME
      );
    }

    // Just update translations without changing language
    if (instance.isInitialized) {
      instance.reloadResources();
    }
  }

  instance.setFormalization = setFormalization;

  return instance;
}

const resourcesInstance = ({
  resources,
  missingKeyHandler,
  userLanguage,
  useFormalLanguageOnLoad,
  initReactI18next,
}: ResourceInstanceParams & SharedInitParams): CustomI18n => {
  const instance = createInstance() as CustomI18n;

  instance.use(initReactI18next);
  instance.use(formalizationPostProcessor);

  instance.init({
    resources,
    fallbackLng: languages.map(languageToIsoLocaleString),
    saveMissing: true,
    lng: languageToIsoLocaleString(userLanguage),
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
    postProcess: useFormalLanguageOnLoad ? [FORMALIZATION_POST_PROCESSOR_NAME] : [],
    ...(missingKeyHandler ? missingKeyHandler : {}),
  });

  return instance;
};

const locizeTranslationsInstance = ({
  userLanguage,
  useFormalLanguageOnLoad,
  locize,
  initReactI18next,
}: {
  locize: LocizeSettings;
} & SharedInitParams): CustomI18n => {
  const instance = createInstance() as CustomI18n;

  instance.use(Locize);
  instance.use(initReactI18next);
  instance.use(formalizationPostProcessor);

  const defaultLanguage = localeToIsoLocaleString(defaultLocale);

  instance.init({
    backend: {
      projectId: locize.projectId,
      apiKey: locize.apiKey,
      referenceLng: defaultLanguage,
      saveMissingTo: 'current',
      fallbackLng: false,
      load: 'currentOnly',
      allowMultiLoading: false,
    },
    saveMissingTo: 'current',
    fallbackLng: false,
    saveMissing: true,
    lng: languageToIsoLocaleString(userLanguage),
    interpolation: {
      escapeValue: false,
    },
    react: {
      bindI18n: 'languageChanged editorSaved',
      useSuspense: locize.useSuspense ?? false,
    },
    postProcess: useFormalLanguageOnLoad ? [FORMALIZATION_POST_PROCESSOR_NAME] : [],
  });

  return instance;
};

const formalizationPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: FORMALIZATION_POST_PROCESSOR_NAME,
  process(value) {
    return formalizeTranslation(value);
  },
};
